import { Network } from 'lucide-react'
import {  ArrowRight } from "lucide-react"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion"
import { useState } from "react"

export default function Home() {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [inputUrl, setInputUrl] = useState("");

    const faqData = [
        {
            question: "How does AI Crawl work?",
            answer: "AI Crawl uses advanced web scraping techniques to extract clean, structured data from any website. It processes the HTML, removes unnecessary elements, and converts the content into formats like Markdown and JSON that are optimized for LLMs and AI applications."
        },
        {
            question: "Is AI Crawl really 100% free?",
            answer: "Yes! Firecrawl is completely free to use with no hidden costs or credit card required. We believe in providing open access to tools that help developers build better AI applications."
        },
        {
            question: "Are there any rate limits?",
            answer: "While Firecrawl is free, we do implement fair usage policies to ensure the service remains available for everyone. Basic accounts have reasonable daily limits that are sufficient for most development needs."
        },
        {
            question: "Can I extract data from any website?",
            answer: "AI Crawl can extract data from most public websites. However, you should always ensure you have the right to scrape content from a website and respect robots.txt files. Some websites with heavy anti-bot measures may be more difficult to crawl."
        }
    ];

    const handleGetStarted = () => {
        const encodedUrl = encodeURIComponent(inputUrl.trim());
        if (currentUser) {
            if (inputUrl) {
                navigate(`/scraper?url=${encodedUrl}`);
            } else {
                navigate('/scraper');
            }
        } else {
            navigate('/sign-up');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleGetStarted();
        }
    };

    return (
        <div className="flex min-h-screen flex-col bg-slate-50">
            {/* Hero section */}
            <main className="flex-1">
                <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="mb-6 inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-800">
                            <span>✨ 100% Free - No Credit Card Required</span>
                        </div>

                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
                            Data Extraction <span className="text-indigo-600">Finetuned for LLM Use</span>
                        </h1>

                        <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600 font-sans">
                            Transform raw web pages into structured data for AI
                            <br />
                            It's completely free.
                        </p>

                        <div className="mx-auto mt-10 max-w-xl">
                            <div className="relative">
                                <Input
                                    type="text"
                                    placeholder="https://example.com"
                                    className="h-12 pr-12 text-base"
                                    value={inputUrl}
                                    onChange={(e) => setInputUrl(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                    <ArrowRight className="h-5 w-5 text-gray-400" />
                                </div>
                            </div>

                            <Button
                                onClick={handleGetStarted}
                                className="mt-4 h-12 w-full bg-gray-900 text-white hover:bg-gray-800"
                            >
                                Start for free
                            </Button>
                        </div>
                    </div>

                    {/* Illustration */}
                    <div className="mt-16 flex justify-center">
                        <div className="relative w-full max-w-4xl">
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                                <div className="overflow-hidden rounded-lg bg-white p-4 shadow-lg">
                                    <div className="flex items-center space-x-1 pb-2">
                                        <div className="h-3 w-3 rounded-full bg-red-500"></div>
                                        <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                                        <div className="h-3 w-3 rounded-full bg-green-500"></div>
                                        <div className="ml-2 h-4 w-3/4 rounded bg-gray-200"></div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="h-4 w-full rounded bg-gray-200"></div>
                                        <div className="h-20 w-full rounded bg-gray-200"></div>
                                        <div className="h-4 w-3/4 rounded bg-gray-200"></div>
                                        <div className="h-4 w-1/2 rounded bg-gray-200"></div>
                                    </div>
                                </div>

                                <div className="overflow-hidden rounded-lg bg-gray-900 p-4 text-white shadow-lg">
                                    <div className="flex items-center space-x-2 pb-2">
                                        <div className="rounded bg-gray-700 px-2 py-1 text-xs">+ 200 Response</div>
                                    </div>
                                    <div className="font-mono text-sm">
                                        <div className="text-gray-400">{"{"}</div>
                                        <div className="ml-4">
                                            <span className="text-green-400">"url":</span>{" "}
                                            <span className="text-amber-400">"https://example.com"</span>,
                                        </div>
                                        <div className="ml-4">
                                            <span className="text-green-400">"json":</span> {"{"}
                                        </div>
                                        <div className="ml-8">
                                            <span className="text-green-400">"markdown":</span>{" "}
                                            <span className="text-amber-400">"# Getting Started..."</span>,
                                        </div>
                                        <div className="ml-8">
                                            <span className="text-green-400">"title":</span> <span className="text-amber-400">"Guide"</span>,
                                        </div>
                                        <div className="ml-8">
                                            <span className="text-green-400">"docs":</span> <span className="text-amber-400">...</span>
                                        </div>
                                        <div className="ml-4">{"}"}</div>
                                        <div className="text-gray-400">{"}"}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg">
                                    <ArrowRight className="h-5 w-5 text-gray-600" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Features section */}
            <section className="bg-white py-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-3xl text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Everything you need to extract web data
                        </h2>
                        <p className="mt-4 text-lg text-gray-600">
                            Simple, powerful tools to transform any website into structured data for your AI applications.
                        </p>
                    </div>

                    <div className="mt-16 grid gap-8 md:grid-cols-3">
                        {/* Feature 1 */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-md bg-indigo-100 text-indigo-600">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="h-6 w-6"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
                                    />
                                </svg>
                            </div>
                            <h3 className="mb-2 text-lg font-medium text-gray-900">Clean HTML Extraction</h3>
                            <p className="text-gray-600">Extract clean, structured HTML from any website with a single API call.</p>
                        </div>

                        {/* Feature 2 */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-md bg-purple-100 text-purple-600">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="h-6 w-6"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
                                    />
                                </svg>
                            </div>
                            <h3 className="mb-2 text-lg font-medium text-gray-900">Markdown Conversion</h3>
                            <p className="text-gray-600">
                                Automatically convert web content to clean, formatted markdown for your LLMs.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-md bg-blue-100 text-blue-600">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="h-6 w-6"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z"
                                    />
                                </svg>
                            </div>
                            <h3 className="mb-2 text-lg font-medium text-gray-900">JSON Structuring</h3>
                            <p className="text-gray-600">
                                Transform web content into structured JSON data ready for your applications.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA section */}
            <section className="bg-indigo-900 py-16 text-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-3xl text-center">
                        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Ready to transform web data?</h2>
                        <p className="mt-4 text-lg text-gray-300">
                            Start using our tools today - completely free, no credit card required.
                        </p>
                        <div className="mt-8">
                            <Button
                                onClick={handleGetStarted}
                                className="bg-indigo-500 text-white hover:bg-indigo-600"
                            >
                                Get Started Now
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="bg-slate-50 py-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-3xl text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            Frequently Asked Questions
                        </h2>
                        <p className="mt-4 text-lg text-slate-600 font-sans">
                            Everything you need to know about Firecrawl
                        </p>
                    </div>

                    <div className="mx-auto mt-12 max-w-3xl">
                        <Accordion type="single" collapsible className="w-full">
                            {faqData.map((faq, index) => (
                                <AccordionItem key={index} value={`item-${index}`}>
                                    <AccordionTrigger className="text-left font-display text-lg">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-slate-600">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col items-center justify-between md:flex-row">
                        <div className="flex items-center">
                            <Network className="h-6 w-6 border-indigo-600" />
                            <span className="ml-2 text-lg font-bold">AI Crawl</span>
                        </div>
                        <div className="mt-4 flex space-x-6 md:mt-0">
                            <Link href="#" className="text-sm text-gray-600 hover:text-gray-900">
                                Documentation
                            </Link>
                            <Link href="#" className="text-sm text-gray-600 hover:text-gray-900">
                                GitHub
                            </Link>
                            <Link href="#" className="text-sm text-gray-600 hover:text-gray-900">
                                Discord
                            </Link>
                            <Link href="#" className="text-sm text-gray-600 hover:text-gray-900">
                                Twitter
                            </Link>
                        </div>
                    </div>
                    <div className="mt-8 border-t border-gray-200 pt-8 text-center">
                        <p className="text-sm text-gray-600">&copy; {new Date().getFullYear()} AI Crawl. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    )
}

